import { useState } from "react";
import axios from "axios";
import { checkPlateStatus, cleanBackendErrorMessage } from '../../../utils/plateValidation';

const INITIAL_FORM_DATA = {
  type_vehicle: "",
  placa: "",
  numberOfHelmets: 0,
};

const patterns = {
  carro: "[A-Z]{3}[0-9]{3}",
  moto: "[A-Z]{3}[0-9]{2}[A-Z]?",
  otro: ".{1,7}",
};

const MiniFormRegister = ({
  onSuccess,
  onError,
  isMobile,
  gridApiRef,
  loadMobileData,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);

  //CHANGE HANDLERS

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData({
      ...formData,
      type_vehicle: newType,
      placa: "",
    });
  };

  const handlePlacaChange = (e) => {
    const rawValue = e.target.value.toUpperCase();
    const type = formData.type_vehicle;
    let validValue = "";

    if (type === "carro") {
      for (let i = 0; i < rawValue.length && i < 6; i++) {
        const char = rawValue[i];
        if (i < 3 && /[A-Z]/.test(char)) validValue += char;
        if (i >= 3 && /[0-9]/.test(char)) validValue += char;
      }
    } else if (type === "moto") {
      for (let i = 0; i < rawValue.length && i < 6; i++) {
        const char = rawValue[i];
        if (i < 3 && /[A-Z]/.test(char)) validValue += char;
        else if (i >= 3 && i < 5 && /[0-9]/.test(char)) validValue += char;
        else if (i === 5 && /[A-Z]/.test(char)) validValue += char;
      }
    } else {
      validValue = rawValue.replace(/[^A-Z0-9]/g, "").slice(0, 7);
    }

    setFormData({
      ...formData,
      placa: validValue,
    });
  };

  //SUBMIT

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const normalizedPlate = formData.placa?.trim().toUpperCase();

      if (!normalizedPlate) {
        throw new Error("Debe ingresar una placa válida.");
      }

      const plateExists = await checkPlateStatus(normalizedPlate);
      if (plateExists) {
        throw new Error(
          `El vehículo con placa ${normalizedPlate} ya se encuentra registrado con estado`
        );
      }

      const token = localStorage.getItem("access_token");

      const payload = {
        ...formData,
        placa: normalizedPlate,
        status: "in",
        entryTime: new Date().toISOString(),
        numberOfHelmets:
          formData.type_vehicle === "carro"
            ? 0
            : formData.numberOfHelmets,
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}/parkingregister/create`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData(INITIAL_FORM_DATA);

      // DESKTOP
      if (!isMobile && gridApiRef?.current?.purgeInfiniteCache) {
        gridApiRef.current.purgeInfiniteCache();
      }

      // MOBILE
      if (isMobile && loadMobileData) {
        await loadMobileData();
      }

      onSuccess?.();

    } catch (error) {
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.errors?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        "Ocurrió un error inesperado.";

      console.error(error);
      onError?.(cleanBackendErrorMessage(backendMessage));
    } finally {
      setLoading(false);
    }
  };

  //RENDER

  return (

    <div className="flex flex-row justify-end my-1 w-full">
      <form
        onSubmit={handleSubmit}
        className="bg-white px-6 py-6 rounded-lg shadow-md w-full border border-slate-200 lg:rounded-lg lg:shadow-md"
      >
        {/* Grid de 3 columnas con proporciones */}
        <div className="grid grid-cols-1 lg:grid-cols-[43%_43%_10%] gap-4 items-end">
          <div className="flex flex-col">
            <label htmlFor="type_vehicle" className="text-slate-700 mb-1 font-medium text-sm lg:text-gray-600 lg:font-normal lg:text-base">
              Tipo de Vehículo
            </label>
            <select
              name="type_vehicle"
              id="type_vehicle"
              value={formData.type_vehicle}
              onChange={handleTypeChange}
              required
              className={`border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 text-slate-700 text-sm lg:border-gray-300 lg:rounded lg:focus:ring-blue-600 lg:text-gray-900
      ${formData.type_vehicle === "" ? "text-slate-400" : "text-slate-700"}`}
            >
              <option value="" disabled hidden>
                Selecciona el tipo de vehículo...
              </option>
              <option value="carro" className="text-gray-900">Carro</option>
              <option value="moto" className="text-gray-900">Moto</option>
              <option value="otro" className="text-gray-900">Otro</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label htmlFor="placa" className="text-slate-700 mb-1 font-medium text-sm lg:text-gray-600 lg:font-normal lg:text-base">
              Placa
              {formData.type_vehicle === 'carro' && <span className="text-xs text-slate-400 ml-2 lg:text-gray-400"></span>}
              {formData.type_vehicle === 'moto' && <span className="text-xs text-slate-400 ml-2 lg:text-gray-400"></span>}
            </label>

            <input
              type="text"
              name="placa"
              id="placa"
              value={formData.placa}
              onChange={handlePlacaChange}
              disabled={!formData.type_vehicle}
              pattern={patterns[formData.type_vehicle]}
              title={
                formData.type_vehicle === "carro"
                  ? "Formato: 3 letras y 3 números (AAA123)"
                  : formData.type_vehicle === "moto"
                    ? "Formato: 3 letras, 2 números y opcional 1 letra (AAA12 o AAA12B)"
                    : "Máximo 7 caracteres"
              }
              className="border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200 text-slate-700 text-sm uppercase placeholder:normal-case lg:border-gray-300 lg:rounded lg:focus:ring-blue-600 lg:text-gray-900"
              placeholder={
                !formData.type_vehicle ? "Seleccione tipo primero" :
                  formData.type_vehicle === "carro" ? "ABC123" :
                    formData.type_vehicle === "moto" ? "ABC12D" : "Cualquier ID"
              }
              required
            />
          </div>
          <button
            type="submit"
            title="Registrar vehículo"
            disabled={loading}
            className="flex items-center justify-center gap-2 bg-white font-semibold py-1 px-3 text-sm rounded-lg border border-slate-300 shadow-sm hover:bg-slate-50 transition duration-300 lg:bg-white text-blue-600 lg:border-0 lg:shadow-sm lg:hover:bg-blue-50"
          >
            {loading ? "Registrando..." : (
              <>
                <span className="text-base">+</span>
                <span className="capitalize">Registrar</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default MiniFormRegister;